import { pageTitle } from '@palaccount/config';
import type { EChartsOption } from 'echarts';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr, Qty } from '@/components/amount';
import { Chart } from '@/components/chart';
import { TableCell } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { signedQtyClass, signedQtyWithUnit } from '@/features/inventory/stock-format';
import { useSessionFilter } from '@/lib/dashboard-filters';
import { useBaseCurrency, useCompanyToday } from '@/lib/session';

import { useItemCategories } from '@/features/inventory/api';

import { useInventoryWidgets } from './api';
import { Board } from './board';
import { INVENTORY_CATALOG } from './catalog';
import { HomeTabs } from './home-tabs';
import { EmptyLine, KpiValue, RankedBars, WidgetTable } from './widgets';

/**
 * DSH-03 — the Inventory Dashboard. No change pills (BR-DSH-03-05); the
 * Low Stock card carries the amber highlight when its count is above zero
 * (BR-DSH-03-04); the three colours on the reorder table are the widget's
 * whole argument (§5i: amber problem, grey reference, blue suggestion).
 */
export function InventoryDashboardPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t, i18n } = useTranslation();
  const today = useCompanyToday();
  /**
   * BR-DSH-00-16 — both choices persist for the browser session and reset to
   * the delivered defaults (30-day window, All categories) on the next
   * sign-in. `days` accepts only the select's three options. `categoryId`
   * accepts '' or a uuid SHAPE rather than membership of the option list,
   * because the list arrives from the server after mount; the shape gate is
   * what matters, since the contract's `z.uuid()` would refuse anything else
   * with a 400 the reader never asked for. A category deleted between
   * sessions restores as a filter matching nothing — the same figures the
   * screen shows when it is deleted mid-session — and picking again clears
   * it, while a malformed stored value never reaches the wire at all.
   */
  const [days, setDays] = useSessionFilter<number>(
    'inventory.days',
    30,
    (value): value is number => value === 7 || value === 30 || value === 90,
  );
  const [categoryId, setCategoryId] = useSessionFilter<string>(
    'inventory.categoryId',
    '',
    (value): value is string =>
      typeof value === 'string' &&
      (value === '' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)),
  );
  const { data, isError, refetch, dataUpdatedAt } = useInventoryWidgets({
    days,
    // '' is "All categories" — the delivered default (§9) — and the request
    // simply carries no categoryId, which the server reads as no filter.
    categoryId: categoryId || undefined,
  });
  const { data: categories } = useItemCategories();

  useEffect(() => {
    document.title = pageTitle(t('dash.tabInventory'));
  }, [t]);

  const trendOption = useMemo<EChartsOption>(() => {
    const buckets = data?.movementTrend.buckets ?? [];
    return {
      grid: { left: 8, right: 8, top: 8, bottom: 24, containLabel: true },
      tooltip: { trigger: 'axis' },
      xAxis: { type: 'category', data: buckets.map((b) => b.label) },
      yAxis: { type: 'value' },
      series: [
        { name: t('dash.stockIn'), type: 'bar', data: buckets.map((b) => Number(b.inflow)), itemStyle: { color: '#10b981' } },
        { name: t('dash.stockOut'), type: 'bar', data: buckets.map((b) => Number(b.outflow)), itemStyle: { color: '#9ca3af' } },
      ],
    };
  }, [data?.movementTrend.buckets, t]);

  const stampTime = new Date(dataUpdatedAt || Date.now()).toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const renderWidget = (id: string) => {
    /**
     * ERR-DSH-03-06 / ERR-DSH-03-07 — "Lost the connection mid-load: `This
     * widget could not load. Click refresh to try again.` inside the affected
     * card", restated in §14's fenced block as "A widget failed to load. Shown
     * inside that widget's card only."
     *
     * This line is DSH-02's fix arriving on the second of the three boards.
     * The sales dashboard had exactly this defect, argued it out in a comment
     * and covered it as ERR-DSH-02-06/-08 — and the same two rules on this
     * screen and on DSH-04 went on being broken, because nothing in the
     * repository named them. Until now the line below was the whole function's
     * first statement, and a FAILED query never sets `data`, so a board that
     * could not load SHIMMERED FOREVER: the one state that asks the reader to
     * wait was the state the screen entered when waiting could not help, and
     * the rule's own remedy ("Click Refresh", and the button is right there in
     * the §14.1.3 stamp) was the one thing it never suggested.
     *
     * ORDER IS THE WHOLE FIX. An errored query is also a query with no data,
     * so if the loading arm came first it would go on swallowing every error
     * exactly as before. `widget-load-failure.test.tsx` pins both directions —
     * the message on failure, and its ABSENCE on a healthy board — because a
     * test that only proved the message appears could not see the ordering.
     *
     * The CARD carries it, never the page: <Board> calls this once per widget,
     * which is what "inside that widget's card only" means, and DSH-03 §14 row
     * 3 is explicit that this board never draws a page-wide banner.
     */
    if (isError) return <EmptyLine text={t('dash.widgetFailed')} />;
    if (!data) return <div className="h-16 animate-pulse rounded bg-muted" aria-busy />;
    switch (id) {
      case 'lowStockCount':
        // BR-DSH-03-04: amber number when above zero; calm when zero.
        /*
         * ERR-DSH-03-02's first half — "Opened it when no item has a reorder
         * level: **Low Stock Items** shows `0` WITH NO HIGHLIGHT". The `tone`
         * below is `undefined` at zero, so the amber number and the amber card
         * border both drop, which is DSH-03 §15's "Zero" edge case stated as a
         * reason: "a count of `0` must drop the amber highlight entirely, so
         * the dashboard is calm when nothing is wrong."
         *
         * The other half is the SERVER's, and it is what makes the count zero
         * in the first place: `reorder_point` is `NOT NULL DEFAULT 0`, so "no
         * reorder level" is stored as `0`, and `DashboardsService.inventory`
         * keeps a row only when `on_hand > 0 && on_hand <= reorder_point` —
         * unsatisfiable at zero. That is §11's "items with no reorder level
         * never appear in the low-stock widgets", enforced by a filter that
         * cannot be told apart from the one EC-19-41 already needed.
         */
        return (
          <>
            <KpiValue value={String(data.lowStockCount)} tone={data.lowStockCount > 0 ? 'warn' : undefined} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.belowReorder')}</p>
          </>
        );
      case 'outOfStockCount':
        return (
          <>
            <KpiValue value={String(data.outOfStockCount)} tone={data.outOfStockCount > 0 ? 'neg' : undefined} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.itemsAtZero')}</p>
          </>
        );
      case 'totalStockValue':
        return (
          <>
            <KpiValue value={data.totalStockValue} currency={baseCurrency} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.currentValuation')}</p>
          </>
        );
      case 'skusTracked':
        return (
          <>
            <KpiValue value={String(data.skusTracked)} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.skusTrackedSub')}</p>
          </>
        );
      case 'pendingReceiptsCount':
        return (
          <>
            <KpiValue value={String(data.pendingReceiptsCount)} />
            <p className="mt-1 text-xs text-muted-foreground">{t('dash.onTheWay')}</p>
          </>
        );
      case 'movementTrend':
        return (
          <>
            <p className="mb-1 flex gap-4 text-xs">
              <span className="text-emerald-700 dark:text-emerald-400">
                {t('dash.stockIn')} <Amount value={data.movementTrend.inTotal} currency={baseCurrency} className="text-xs" />
              </span>
              <span className="text-muted-foreground">
                {t('dash.stockOut')} <Amount value={data.movementTrend.outTotal} currency={baseCurrency} className="text-xs" />
              </span>
            </p>
            {data.movementTrend.buckets.length === 0 ? (
              <EmptyLine text={t('dash.noData')} />
            ) : (
              <Chart option={trendOption} height={160} />
            )}
          </>
        );
      case 'valueByCategory':
        return <RankedBars rows={data.valueByCategory} />;
      case 'valueByWarehouse':
        // null = the company does not hold multi_branch, so the server
        // computed nothing (§1.5.3). The board never draws this widget in that
        // case — the catalogue's `module` gate removed it — but the type says
        // it can be absent and pretending otherwise with a `!` would be the
        // client asserting a fact only the server holds.
        return data.valueByWarehouse === null ? (
          <EmptyLine text={t('dash.noData')} />
        ) : (
          <RankedBars rows={data.valueByWarehouse} />
        );
      case 'lowStock':
        /*
         * ERR-DSH-03-02's second half — the reorder table's own empty message.
         *
         * THE SPEC'S WORDING AND THE BUILD'S DIFFER BY ONE NOUN, deliberately.
         * §14 quotes "Nothing is below its reorder point."; `dash.noLowStock`
         * reads "No item is below its reorder level." The screen calls the
         * field a reorder LEVEL everywhere the user can see it — the column
         * header is `dash.colReorderLevel`, and §14's own remedy column says
         * "Set a reorder LEVEL on each item that matters" two lines above the
         * quoted sentence. The spec uses both nouns for one field; the build
         * picks the one it uses in the column beside the message. Matching the
         * §14 string verbatim would put "reorder point" in the empty state and
         * "Reorder level" in the header of the very table it replaces.
         *
         * This is NOT a licence to drift from quoted strings — it is the
         * narrow case where §14 contradicts itself within one row, and the
         * choice is recorded rather than left for a later reader to "correct".
         */
        return data.lowStock.length === 0 ? (
          <EmptyLine text={t('dash.noLowStock')} />
        ) : (
          <WidgetTable head={[t('dash.colItem'), t('dash.colQty'), t('dash.colReorderLevel'), t('dash.colSuggested'), '']}>
            {data.lowStock.map((row) => (
              <tr key={row.itemId} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.name}</p>
                  <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.sku} /></p>
                </TableCell>
                {/* `N-4` on all three. BR-DSH-03-19 already forbids adding
                    across items here; a bare column of numbers is the thing
                    `N-14` says invites exactly that. One unit describes the
                    row: on hand, the reorder point and the suggested order are
                    the same shelf measured the same way. */}
                <TableCell className="px-0 text-end text-amber-700 dark:text-amber-400"><Qty value={row.qty} precision={row.unitPrecision} unit={row.unitSymbol} /></TableCell>
                <TableCell className="px-0 text-end text-muted-foreground"><Qty value={row.reorderLevel} precision={row.unitPrecision} unit={row.unitSymbol} /></TableCell>
                <TableCell className="px-0 text-end font-medium text-primary"><Qty value={row.suggested} precision={row.unitPrecision} unit={row.unitSymbol} /></TableCell>
                {/*
                  THE ARROW'S PERMISSION ROW, AND TWO RULES DELIBERATELY NOT
                  CLAIMED FOR IT.

                  ERR-DSH-03-04 wants a message — "You do not have    rule-coverage:ignore
                  permission to open this item." — and ERR-DSH-03-08 is    rule-coverage:ignore
                  its fenced restatement, "A row's item cannot be opened for
                  lack of permission."

                  NEITHER CAN HAPPEN HERE, so neither is built and neither is
                  cited. `DashboardsService.inventory()` demands
                  `inventory.view` before it computes a figure, and reading an
                  item demands the identical `inventory.view` — so a reader who
                  can see this arrow can always follow it, and a reader who
                  cannot never reaches the board. §6's own answer is the same
                  one: "Hidden for a user without item permission", satisfied
                  by the whole screen being absent for them.

                  It is recorded rather than counted because ONE permission on
                  both sides is not the same thing as the per-document check
                  those two rules describe. Should the two ever part — an
                  item-level rule, a narrower grant — the arrow needs a fence
                  of its own and the message becomes reachable. That is the
                  same position `sales-dashboard-page.tsx` takes on
                  ERR-DSH-02-05, and for the same reason.

                  ERR-DSH-03-09 — "an item has stock but no unit of    rule-coverage:ignore
                  measure, so its quantity cannot be formatted" — cannot arise
                  either, and this time by the schema: `items.stock_unit_id` is
                  `notNull()`, and the query joins `units` on it with an INNER
                  join. There is no item without a unit for `<Qty>` to fail on.

                  ── AND AS OF 27 AUG THE SPEC SAYS SO TOO (RULING D45) ─────

                  All three are now RETIRED by the document. Delegated ruling
                  D45 ordered a retirement notice onto each, and each notice
                  states the precondition that cannot occur — the single
                  permission for the first two, the NOT NULL column plus the
                  INNER join for the third — rather than merely declaring the
                  rule dead.

                  THAT IS WHY THE RULING WAS NEEDED AT ALL, and it is worth
                  being precise about: the three paragraphs above were already
                  true and already here, and the queue did not care. A
                  `rule-coverage:ignore` mark stops an id being counted as
                  CITED; nothing in it touches the UNNAMED side. So an honest
                  disposition in code leaves the rule sitting in a countdown
                  no wave can ever work it out of, and only a notice in the
                  specification removes it.

                  THE MARKS ABOVE STAY. A retired rule MAY be cited — that is
                  the whole difference between the retired list and the
                  prohibited one — so the marks no longer protect a counter.
                  They protect the CLAIM: every id above sits inside a sentence
                  explaining why nothing enforces it, and an unmarked id in
                  such a sentence would record this file as the place the rule
                  is enforced.

                  AND THE REVIVAL CONDITIONS ARE IN THE DOCUMENT, NOT ONLY
                  HERE. Each notice ends by naming what re-opens it: split the
                  two permissions, make the stock-unit column nullable, or make
                  that join outer. A migration doing any of those makes the
                  retirement false, and the person writing it reads the
                  document rather than this comment.
                */}
                <TableCell className="px-0 text-end">
                  <Link className="text-primary" to={`/inventory/items/${row.itemId}`} aria-label={t('dash.openDocument')}>›</Link>
                </TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'outOfStock':
        return data.outOfStock.length === 0 ? (
          <EmptyLine text={t('dash.noOutOfStock')} />
        ) : (
          <WidgetTable head={[t('dash.colItem'), t('dash.colDaysOut'), t('dash.colLastRestock'), '']}>
            {data.outOfStock.map((row) => (
              <tr key={row.itemId} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.name}</p>
                  <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.sku} /></p>
                </TableCell>
                {/* BR-DSH-03-11's screen half: days out is "always drawn in
                    RED with NO SEVERITY RAMP" — a flat `text-red-600`, and
                    deliberately not `overdueTone()`, which the overdue
                    receivables column uses. Out of stock is out of stock;
                    the spec gives this one no grades. */}
                <TableCell className="px-0 text-end text-red-600 dark:text-red-400"><Ltr value={`${row.daysOut}d`} className="font-mono" /></TableCell>
                <TableCell className="px-0 text-end text-muted-foreground">
                  {row.lastRestock ? <Ltr value={row.lastRestock} className="font-mono" /> : '—'}
                </TableCell>
                <TableCell className="px-0 text-end">
                  <Link className="text-primary" to={`/inventory/items/${row.itemId}`} aria-label={t('dash.openDocument')}>›</Link>
                </TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'deadStock':
        /*
         * ERR-DSH-03-05 — "Expected an open arrow on Dead Stock or Pending
         * Goods Received: NOTHING HAPPENS; THERE IS NO ARROW ON THOSE TABLES."
         *
         * THE ABSENCE IS THE ENFORCEMENT, so it is written down here and in
         * `pendingReceipts` below: these two `head` arrays end on a real
         * column, while `lowStock` and `outOfStock` end on a `''` header whose
         * cell carries the `›` link. That trailing empty string is the entire
         * visible difference between a table with a row action and one
         * without, which makes "add the arrow the other tables have" a very
         * easy and very wrong tidy-up. It is the same shape DSH-04 states for
         * its combined Upcoming table (BR-DSH-04-27 / ERR-DSH-04-06) and for
         * the same reason: §14's remedy is "Open the item or the purchase
         * order from the side menu", not a second route to it from here.
         *
         * `inventory-dashboard-errors.test.tsx` asserts the arrow's ABSENCE on
         * these two tables in the same run that asserts its PRESENCE on the
         * other two — one without the other proves only that some tables have
         * arrows, which was never in doubt.
         */
        return data.deadStock.length === 0 ? (
          <EmptyLine text={t('dash.noDeadStock')} />
        ) : (
          <WidgetTable head={[t('dash.colItem'), t('dash.colDaysIdle'), t('dash.colQty')]}>
            {data.deadStock.map((row) => (
              <tr key={row.itemId} className="border-t text-muted-foreground">
                <TableCell className="px-0">
                  <p className="truncate">{row.name}</p>
                  <p className="font-mono text-xs"><Ltr value={row.sku} /></p>
                </TableCell>
                <TableCell className="px-0 text-end"><Ltr value={`${row.daysIdle}d`} className="font-mono" /></TableCell>
                {/* BR-DSH-03-12 / BR-DSH-03-13's idle stock is still stock,
                    and stock is measured in something (`N-4`).

                    BR-DSH-03-18 and BR-DSH-03-20 are what `<Qty>` is doing on
                    every quantity cell of this screen: the decimals come from
                    the item's own unit of measure and "the unit must be part
                    of the displayed value". -20 is the licence for it — a
                    SINGLE item's quantity may carry its own unit, "because no
                    addition takes place". Where addition would take place,
                    BR-DSH-03-19 sends the figure to money instead. */}
                <TableCell className="px-0 text-end"><Qty value={row.qty} precision={row.unitPrecision} unit={row.unitSymbol} /></TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'pendingReceipts':
        // ERR-DSH-03-05's other table — four columns, no trailing arrow cell.
        // See the note on `deadStock` above; the two are one rule.
        return data.pendingReceipts.length === 0 ? (
          <EmptyLine text={t('dash.noPendingReceipts')} />
        ) : (
          <WidgetTable head={[t('dash.colItem'), t('dash.colExpectedQty'), t('dash.colExpectedDate'), t('dash.colSupplier')]}>
            {data.pendingReceipts.map((row, index) => (
              <tr key={index} className="border-t">
                <TableCell className="px-0">
                  <p className="truncate">{row.item}</p>
                  <p className="font-mono text-xs text-muted-foreground"><Ltr value={row.reference} /></p>
                </TableCell>
                {/* What is still to arrive, in the unit it was ordered in —
                    a PO line carries no unit of its own (`N-4`). */}
                <TableCell className="px-0 text-end"><Qty value={row.expectedQty} precision={row.unitPrecision} unit={row.unitSymbol} /></TableCell>
                <TableCell className={cn('px-0 text-end', row.expectedDate && row.expectedDate < today ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                  {row.expectedDate ? <Ltr value={row.expectedDate} className="font-mono" /> : '—'}
                </TableCell>
                <TableCell className="px-0 text-end text-muted-foreground">{row.supplier}</TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      case 'recentAdjustments':
        return data.recentAdjustments.length === 0 ? (
          <EmptyLine text={t('dash.noAdjustments')} />
        ) : (
          <WidgetTable head={[t('dash.colItem'), t('dash.colAdjustment'), t('dash.colReason'), t('dash.colUser'), t('dash.colWhen')]}>
            {data.recentAdjustments.map((row, index) => (
              <tr key={index} className="border-t">
                <TableCell className="px-0 truncate">{row.item}</TableCell>
                {/* BR-DSH-03-15: "a stock adjustment row must SHOW THE SIGN of
                    the change, and must draw the value GREEN when it increased
                    stock and RED when it decreased it" — `signedQtyWithUnit`
                    writes the sign, `signedQtyClass` picks the colour, and
                    both read the same printed figure.

                    The signed movement, through INV-06's own formatter: the
                    sign is read off the figure that is PRINTED, so a value
                    rounding away to nothing cannot acquire a "+". The server
                    now sends the stored decimal string rather than a
                    pre-signed one squeezed through a JS number. */}
                <TableCell className={cn('px-0 text-end', signedQtyClass(row.change))}>
                  <Ltr
                    value={signedQtyWithUnit(row.change, row.unitPrecision, row.unitSymbol)}
                    className="font-mono tabular-nums"
                  />
                </TableCell>
                {/* BR-DSH-03-16: the reason comes from INV-08's two fixed
                    lists, floored by the `stock_adjustments_reason_by_direction`
                    CHECK constraint — never free typing, on any path.
                    BR-DSH-03-17: the user who made it and the time it was
                    made, both recorded AND both shown, which is the half of
                    that rule this table is responsible for. */}
                <TableCell className="px-0 text-end text-muted-foreground">{row.reason}</TableCell>
                <TableCell className="px-0 text-end">{row.user}</TableCell>
                <TableCell className="px-0 text-end text-xs text-muted-foreground"><Ltr value={row.when} className="font-mono" /></TableCell>
              </tr>
            ))}
          </WidgetTable>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <HomeTabs />
      <h1 className="text-xl font-semibold">{t('dash.tabInventory')}</h1>
      <Board
        board="inventory"
        catalog={INVENTORY_CATALOG}
        stamp={{ text: t('dash.updatedAt', { time: stampTime }), refresh: () => void refetch() }}
        /**
         * TWO FILTERS, WHERE §5a NAMES FOUR. Category and the date window are
         * built; Warehouse and Stock status are not, and the two absences are
         * different in kind:
         *
         * The Warehouse filter is §5a row 1, gated by the same multi-branch
         * flag as widget 10 — the knot `catalog.ts` records at `byBranch`:
         * whether multi-branch is a dashboard reading or a product dimension
         * is the owner's open decision, and this filter rides it.
         *
         * The Stock status filter (§5a row 3) is marked [NOT IN PROTOTYPE]
         * with proposed labels; nothing blocks it but nobody has built it.
         *
         * BR-DSH-03-23's warehouse half IS built and cited, on the server, at
         * the `wh` parameter every stock query in that method threads — the
         * screen simply offers no way to set it yet.
         *
         * The CATEGORY select below is §5a row 2. Its options are the MD-04
         * tree, flat, the same way INV-05's filter offers them; picking a
         * PARENT is answered with its whole subtree, and that widening —
         * BR-DSH-03-22 — is the server's, in `DashboardsService.inventory()`,
         * where one recursive walk feeds every widget's predicate. Nothing
         * here compares category ids, which is the point: a screen-side
         * "help" that filtered rows to the picked id would re-create the
         * exact equality the rule forbids.
         */
        filters={
          <>
            <label className="flex items-center gap-2 text-sm">
              <span className="sr-only">{t('stock.filterCategory')}</span>
              <select
                className="rounded-md border bg-background px-2 py-1.5"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">{t('stock.filterCategoryAll')}</option>
                {(categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <span className="sr-only">{t('dash.filterDateRange')}</span>
              <select
                className="rounded-md border bg-background px-2 py-1.5"
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
              >
                <option value={7}>{t('dash.last7')}</option>
                <option value={30}>{t('dash.last30')}</option>
                <option value={90}>{t('dash.last90')}</option>
              </select>
            </label>
          </>
        }
        renderWidget={renderWidget}
      />
    </div>
  );
}
